// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
import React from 'react'
import { Button, Modal, Tabs, Tag } from 'antd'
import './help-and-support-modal.less'

const HelpAndSupportModal = ({ visible = false, onChange }) => {
  const footer = (
    <Button type={'primary'} onClick={onChange}>
      Done
    </Button>
  )
  const title = <span className={'help-title'}>Help and support</span>
  const startUploadText = (
    <div className={'help-content'}>
      <header className={'help-content-header'}>
        <p>
          The start of an upload is determined by the capture date and time of each task. The rules below will be
          followed unless any of the <em className={'help-em'}>special situations</em> specified are happening. The
          default upload behavior is as follows:
        </p>
      </header>
      <ol className={'help-content-detail'}>
        <li>
          <nav>1 - For a task with capture day in the past</nav>
          <p>
            This task will begin uploading immediately after the task is added to the upload queue. For example, if you
            are currently on 01/01/2020 and the selected capture date is on 31/12/2019 ~ 02/01/2020 and at time 09:00 AM
            ~ 06:00 PM, the upload task for 31/12/2019 will begin immediately.
          </p>
        </li>
        <li>
          <nav>2 - For a task with capture day on the same day as the task creation date.</nav>
          <p>
            This task will begin after the end of the capture time of each capture day. For example, if you are
            currently on 01/01/2020, 03:00 PM and the capture date is on 01/01/2020 ~ 04/01/2020 and at time 09:00 AM ~
            06:00 PM, the upload for the first day will begin at 01/01/2020, 06:00 PM.
          </p>
        </li>
        <li>
          <nav> 3 - For a task with capture day in the future</nav>
          <p>
            This task will begin after the end of the capture time on each capture day. For example, if you are on
            01/01/2020 and the capture date is on 02/01/2020 ~ 04/01/2020 and at time 09:00 AM ~ 06:00 PM, the upload
            for the first day will begin at 02/01/2020, 06:00 PM, and so on and so forth.
          </p>
        </li>
        <li>
          <header>
            <nav>
              <em className={'help-em'}>Special Situations</em>
            </nav>
            <p>The upload will happen in the manner stated above except in situations when,</p>
          </header>
        </li>
        <li>
          <nav>1 - The camera is powered off when the upload time is begin.</nav>
          <p>If so, the upload will begin when the camera is power on again.</p>
        </li>
        <li>
          <nav> 2 - The camera is offline when the upload should begin. </nav>
          <p>If so, the upload will begin when the camera is online again</p>
        </li>
        <li>
          <nav> 3 - The camera is being used for recording when the upload should begin. </nav>
          <p>
            Currently, the camera cannot be used to record and transmit images concurrently. If so, the upload will
            begin after the camera has ended the recording operations.
          </p>
        </li>
        <li>
          <nav> 4 - There are other older tasks which are ongoing for the same cameraID </nav>
          <p>
            Upload activities are prioritised to complete the oldest task first. If so, the upload for the newest task
            will begin when all the oldest uploads are completed or has been stopped.
          </p>
        </li>
      </ol>
    </div>
  )
  const endUploadText = (
    <div className={'help-content'}>
      <p>
        The upload will end on the last day of the specified capture date. For example, if the selected capture date is
        on 31/12/2019 ~ 02/01/2020 and at time 09:00 AM ~ 06:00 PM, the upload task will end at 06:00 PM on 02/01/2020.
      </p>
    </div>
  )
  const uploadStagesText = (
    <div className={'help-content'}>
      <header className={'help-content-header'}>
        <p>The upload queue is divided into 3 separated stages. Below is the explanation for each stage.</p>
      </header>
      <table className={'help-content-detail'}>
        <thead>
          <tr>
            <th>Status</th>
            <th>Definitions</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Not Started</td>
            <td>
              This stage contains tasks whose upload time has not yet arrived. The tasks will remain here until the
              first upload task has started. The task in this stage can be modified or deleted.
            </td>
          </tr>
          <tr>
            <td>In Progress</td>
            <td>
              This stage contains tasks whose upload start time has arrived. A task will be moved here once the first
              upload task has started. Once a task day has been uploaded, a link to the uploaded image track will be
              shown.
            </td>
          </tr>
          <tr>
            <td>Completed</td>
            <td>
              This stage contains tasks whose upload has ended. A task will be moved here once the upload in all of
              capture days has been completed. the tasks in this stage will display an overall status corresponding to
              the upload result.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
  const uploadStatusText = (
    <div className={'help-content'}>
      <header className={'help-content-header'}>
        <ul>
          <li>
            Each job listed under Upload Queue has upload tasks in days. Each upload task has status indicates which step the task is currectly at.
          </li>
        </ul>
      </header>
      <section className={'help-content-detail'}>
        <h3>Job Stages</h3>
        <table>
          <thead>
            <tr>
              <th>Stage</th>
              <th> Definitions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <Tag color={''}>Completed</Tag>
              </td>
              <td>All the capture dates have completed for this job.</td>
            </tr>
            <tr>
              <td>
                <Tag color={'orange'}>Stopped</Tag>
              </td>
              <td>
                The job was stopped before its last capture day during the “in progress” stage. 
                The completed capture days will continue process uploaded photos and the photo link asssocaited to each task will be available.
              </td>
            </tr>
          </tbody>
        </table>
        <h3> Status of tasks under each job</h3>
        <table>
          <thead>
            <tr>
              <th>Status</th>
              <th> Definitions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <Tag color={'blue'}>Pending</Tag>
              </td>
              <td>The task has not start.</td>
            </tr>
            <tr>
              <td>
                <Tag color={'green'}>Completed</Tag>
              </td>
              <td>The task is completed. All photos has been uploaded and processed for this task.</td>
            </tr>
            <tr>
              <td>
                <Tag color={'cyan'}>UploadingFromKC</Tag>
              </td>
              <td>
                {' '}
                The task is in progress. Photos are being uploaded from cameras to CMT backend.
              </td>
            </tr>
            <tr>
              <td>
                <Tag color={'cyan'}>Stitching</Tag>
              </td>
              <td> The task is in progress. Photos are being stitched.</td>
            </tr>
            <tr>
              <td>
                <Tag color={'cyan'}>UploadingToKV</Tag>
              </td>
              <td> The task is in progress. Stitched/processed photos are being uploaded to KartaView. Seqeunce link will be avaliable once finished. Note: KartaView may have processing time before the link can be accessed.</td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>
  )
  const notStartText = (
    <div className={'help-content'}>
      <header className={'help-content-header'}>
        <p>There could be a few reasons:</p>
      </header>
      <ol className={'help-content-detail'}>
        <li>
          <nav>1 - The camera is powered off when the upload time is set to begin.</nav>
          <p>If so, the upload will begin when the camera is power on again.</p>
        </li>
        <li>
          <nav>2 - The camera is offline when the uploadis set to begin.</nav>
          <p>If so, the upload will begin when the camera is online again</p>
        </li>
        <li>
          <nav>3 - The camera is being used for recording when the upload is set to begin.</nav>
          <p>
            Currently, the camera cannot be used to record and transmit images concurrently. If so, the upload will
            begin after the camera has ended the recording operations.
          </p>
        </li>
        <li>
          <nav>4 - There are other older tasks which are ongoing for the same cameraID</nav>
          <p>
            Upload activities are prioritised to complete the oldest task first. If so, the upload for the newest task
            will begin when all the older uploads are completed or has been stopped.
          </p>
        </li>
      </ol>
    </div>
  )
  const helpTex = [
    { title: <span className={'tab-title'}>Start of Upload</span>, content: startUploadText },
    { title: <span className={'tab-title'}>End of Upload</span>, content: endUploadText },
    { title: <span className={'tab-title'}>Upload Stages</span>, content: uploadStagesText },
    { title: <span className={'tab-title'}>Upload Status</span>, content: uploadStatusText },
    { title: <span className={'tab-title'}>Why is my upload not starting?</span>, content: notStartText }
  ]
  return (
    <Modal
      visible={visible}
      centered={true}
      title={title}
      footer={footer}
      onCancel={onChange}
      width={'800px'}
      // bodyStyle={{borderTop: '4px dashed red'}}
      wrapClassName={'help-modal'}
    >
      <Tabs>
        {helpTex.map((item, index) => (
          <Tabs.TabPane tab={item.title} key={index}>
            {item.content}
          </Tabs.TabPane>
        ))}
      </Tabs>
    </Modal>
  )
}
export { HelpAndSupportModal }
